import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./style.module.scss";
import { ReactComponent as HomeIcon } from "@/assets/icons/header/header-home.svg";
import { ReactComponent as OutIcon } from "@/assets/icons/header/header-out.svg";
import { ReactComponent as SettingIcon } from "@/assets/icons/header/header-setting.svg";
import { ReactComponent as OrderIcon } from "@/assets/icons/header/header-order.svg";
import { ReactComponent as AccelerationCHSvgText } from "@/assets/images/space/acceleration-text.svg";
import VipInfoBg from "@/assets/images/space/vip-info-bg.png";
import { loginStore } from "@/modules/login/store";
import { accelerationStore } from "@/stores/acceleration";
import { useHistory } from "react-router";
import Trigger from "rc-trigger";
import formatDate from "@/pages/Space/utils";
import { observer } from "mobx-react-lite";
import CopyToClipboard from "react-copy-to-clipboard";
import { useOwnToast } from "@/utils/misc";
import VipInfoCard from "./VipInfoCard";
import { Box, useBoolean } from "@chakra-ui/react";
import { userAssetsStore } from "@/stores/userAssets";
import VipInfoCardPro from "./VipInfoCardPro";
import { HeartCoinPayment } from "@/modules/payment/HeartCoinPayment";
import AcceleratePayment from "@/modules/payment/AcceleratePayment";

interface IMyGadget {
  openNewPageWhenJump?: boolean;
  triggerStyle?: React.CSSProperties;
}

const MyGadget: React.FC<IMyGadget> = ({ openNewPageWhenJump = false, triggerStyle = {} }) => {
  const [visible, setVisible] = useState<boolean>(false);
  const { data: accelerationData, isVip, isDayVip } = accelerationStore;
  const [accelerationOpen, accelerationOp] = useBoolean(false);
  const history = useHistory();
  const info = loginStore.userState.userInfo;
  const triggerDom = useRef(null);
  const [mountTarget, setMountTarget] = useState<null | HTMLElement>(null);
  useEffect(() => {
    if (triggerDom.current) setMountTarget(triggerDom.current);
  }, []);
  const toast = useOwnToast();

  const copyIdCallback = () => {
    toast({
      description: "复制用户ID到剪贴板",
      status: "success",
      duration: 1500,
      position: "top",
    });
  };

  const loginOut = () => {
    loginStore.logout().then(() => {
      if (/(noPermission|my_space)/.test(history.location.pathname)) {
        history.replace("/");
      }
    });
  };

  const jumpToSpace = () => {
    console.log("openNewPageWhenJump: ", openNewPageWhenJump);
    if (openNewPageWhenJump) {
      window.open("/my_space", "_blank");
      return;
    }
    history.push("/my_space");
  };
  const jumpToOrder = () => {
    if (openNewPageWhenJump) {
      window.open("/order", "_blank");
      return;
    }
    history.push("/order");
  };
  const jumpToSetting = () => {
    if (openNewPageWhenJump) {
      window.open("/setting", "_blank");
      return;
    }
    history.push("/setting");
  };

  const content = useMemo(() => {
    return (
      <div className={styles["content-wrapper"]}>
        <div className={styles["self-info-wrapper"]}>
          <div className={styles["portrait-wrapper"]}>
            <img src={info.avatar} alt="" />
          </div>
          <CopyToClipboard text={`${info.userId}`} onCopy={copyIdCallback}>
            <div className={styles["name-and-id"]}>
              <div className={styles["name"]}>{info.nickname}</div>
              <div className={styles["id"]}>ID: {info.userId}</div>
            </div>
          </CopyToClipboard>
        </div>
        <Box p="16px" w="100%">
          {accelerationData.subscribed === 1 ? (
            <VipInfoCardPro
              title={`画宇宙会员${isDayVip ? "(试用)" : ""}`}
              info={`${formatDate(
                new Date(Number(accelerationData.expireTime) * 1000),
                "YYYY-MM-DD 到期",
              )}`}
              btnText="续费"
              onBtnClick={accelerationOp.on}
              mb="8px"
            />
          ) : (
            <VipInfoCard
              title="画宇宙免费用户"
              info="成为会员，生成不限量、不排队"
              btnText="开通"
              onBtnClick={accelerationOp.on}
              mb="8px"
            />
          )}
          <VipInfoCard
            title={`剩余付费画力星 ⭐：${userAssetsStore.heartCoinInfo.payed}`}
            info={isVip ? "" : `另有${userAssetsStore.heartCoinInfo.free}免费星`}
            tooltip="免费用户每日登录可获赠 30 免费画力星⭐， 仅使用画宇宙系列模型功能"
            btnText="购买"
            onBtnClick={() => userAssetsStore.setHeartCoinPayModalOpen(true)}
          />
        </Box>

        <div className={styles["link-btn-wrapper"]}>
          <div className={styles["link-btn"]} onClick={jumpToSpace}>
            <HomeIcon className={styles["link-btn-icon"]} />
            我的空间
          </div>
          <div className={styles["link-btn"]} onClick={jumpToOrder}>
            <OrderIcon className={styles["link-btn-icon"]} />
            我的订单
          </div>
          <div className={styles["link-btn"]} onClick={jumpToSetting}>
            <SettingIcon className={styles["link-btn-icon"]} />
            账号设置
          </div>
          <div className={styles["divider-wrapper"]}>
            <div className={styles["divider"]}></div>
          </div>
          <div className={styles["link-btn"]} onClick={loginOut}>
            <OutIcon className={styles["link-btn-icon"]} />
            退出账号
          </div>
        </div>
      </div>
    );
  }, [info, history, accelerationData, userAssetsStore.heartCoinInfo, isVip]);
  const handleVisibleChange = (visible: boolean) => {
    setVisible(visible);
  };
  const handleTriggerClicked = () => {
    setVisible(true);
  };

  return (
    <>
      <Trigger
        getPopupContainer={() => {
          return mountTarget ? mountTarget : document.body;
        }}
        popupVisible={visible}
        action={["click"]}
        popup={content}
        onPopupVisibleChange={handleVisibleChange}
        popupAlign={{
          points: ["tr", "br"],
          offset: [2, 13],
        }}
      >
        <div
          ref={triggerDom}
          className={styles["trigger-wrapper"]}
          onClick={handleTriggerClicked}
          style={{ ...triggerStyle }}
        >
          <img src={info.avatar} alt="" />
        </div>
      </Trigger>
      <HeartCoinPayment />
      <AcceleratePayment open={accelerationOpen} onClose={accelerationOp.off} />
    </>
  );
};

export default observer(MyGadget);
